import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PlaysManagementModal } from '@/components/layout/sidebar/PlaysManagementModal';
import { useCustomAuth } from '@/contexts/CustomAuthContext';
import useUserPlan from '@/hooks/useUserPlan';
import { AutoRechargeConfig, invoiceService } from '@/services/invoiceService';
import { subscriptionService } from '@/services/subscriptionService';

import { hasExhaustedPaidPlays } from '@/utils/playsBalance';

export function ExhaustedPlaysModal() {
  const { user, plan, refreshUser, isLoading } = useCustomAuth();
  const { userPlan, idStripeProduct, isError: planError } = useUserPlan();
  const queryClient = useQueryClient();
  const exhausted = !isLoading && user?.nivel !== 'member' && hasExhaustedPaidPlays(plan);
  const [dismissed, setDismissed] = useState(false);
  const [extrasOpen, setExtrasOpen] = useState(false);
  const [confirmRenewal, setConfirmRenewal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [recharge, setRecharge] = useState<AutoRechargeConfig | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [pendingRecoveryFailed, setPendingRecoveryFailed] = useState(false);
  const [surplusPending, setSurplusPending] = useState(false);
  const [pending, setPending] = useState<ReturnType<typeof subscriptionService.getPendingCheckout>>(null);
  const customer = user?.id_Stripe_Client;
  const renewal = userPlan?.data?.renewal;
  const validQuote = renewal && renewal.product_id === idStripeProduct && renewal.currency?.toLowerCase() === 'brl' &&
    Number.isSafeInteger(renewal.amount_cents) && renewal.amount_cents > 0 && Number.isSafeInteger(renewal.plays) && renewal.plays > 0;
  const refreshBalances = async () => {
    await Promise.all([refreshUser(), queryClient.invalidateQueries({ queryKey: ['userPlan', user?.id_user] }), queryClient.invalidateQueries({ queryKey: ['financeDashboard', customer] })]);
  };
  useEffect(() => { setDismissed(false); }, [customer]);
  useEffect(() => {
    if (!exhausted) { setDismissed(false); setConfirmRenewal(false); }
  }, [exhausted, customer]);
  useEffect(() => {
    if (!exhausted || dismissed || !customer) return;
    let active = true;
    setChecking(true);
    setRecharge(null);
    const load = async () => {
      const [response, recovered] = await Promise.all([
        invoiceService.getAutoRecharge(customer),
        subscriptionService.recoverPendingCheckout(customer),
      ]);
      if (!active) return;
      setChecking(false);
      if (response.success === true && response.data) { setRecharge(response.data); setError(response.data.last_error || ''); }
      else setError(response.message || 'Não foi possível consultar a recarga automática. Atualize a página para tentar novamente.');
      setPendingRecoveryFailed(recovered.success !== true || !recovered.data);
      if (recovered.success === true && recovered.data) {
        setSurplusPending(recovered.data.kind === 'surplus' || !!sessionStorage.getItem(`hostvsl:surplus-purchase:${customer}`));
        setPending(recovered.data.kind === 'subscription' ? recovered.data.pending : null);
      } else setError(recovered.message || 'Não foi possível consultar o pagamento pendente. Tente novamente antes de pagar.');
    };
    void load();
    const interval = window.setInterval(() => { void load(); void refreshUser(); }, 10000);
    return () => { active = false; window.clearInterval(interval); };
    // refreshUser is recreated by the auth provider; including it would restart polling on every refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exhausted, dismissed, customer]);

  const handleResumeRecharge = async () => {
    if (!customer || busy) return;
    setBusy(true);
    try {
      const response = await invoiceService.resumeAutoRecharge(customer);
      if (response.data) setRecharge(response.data);
      if (response.success !== true) setError(response.message || 'Recarga aguardando confirmação. Consulte a mesma tentativa novamente.');
      else { setError(response.data?.last_error || ''); await refreshBalances(); }
    } finally { setBusy(false); }
  };
  const handleRenew = async () => {
    if (!customer || busy || recharge?.processing || pendingRecoveryFailed || surplusPending || checking || !recharge || planError || (!pending && !validQuote)) return;
    if (pending && pending.action !== 'renew_now') {
      setError('Há outra assinatura aguardando confirmação. Consulte esse pagamento no checkout antes de renovar.'); return;
    }
    setBusy(true);
    try {
      const result = await subscriptionService.checkout(customer, pending || { product_id: renewal!.product_id, currency: 'brl', action: 'renew_now', expected_amount_cents: renewal!.amount_cents });
      setPending(subscriptionService.getPendingCheckout(customer));
      if (result.success !== true) {
        setError(result.message || 'Renovação aguardando confirmação. Consulte a mesma tentativa novamente.');
        if (result.definitive === true || result.retryable === false) { setConfirmRenewal(false); await queryClient.invalidateQueries({ queryKey: ['userPlan', user?.id_user] }); }
        return;
      }
      await refreshBalances();
      toast.success('Plano renovado. Seu novo ciclo e os plays foram atualizados.');
      setDismissed(true);
      setConfirmRenewal(false);
    } finally { setBusy(false); }
  };
  const blocked = busy || checking || pendingRecoveryFailed || !recharge || recharge.processing || !!pending;
  return <>
    <Dialog open={!!exhausted && !dismissed && !extrasOpen} onOpenChange={open => { if (!open && !busy) setDismissed(true); }}>
      <DialogContent className="max-w-lg rounded-2xl p-6">
        <DialogHeader>
          <DialogTitle>Seus plays acabaram</DialogTitle>
          <DialogDescription>A reprodução dos vídeos está pausada porque o saldo do plano e dos plays extras acabou. Seus vídeos, métricas, gerenciamento e novos envios continuam disponíveis.</DialogDescription>
        </DialogHeader>
        {checking && <p className="text-sm text-muted-foreground">Consultando recarga automática...</p>}
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        {recharge?.processing ? <div className="space-y-3 rounded-xl bg-blue-50 p-4 text-sm">
          <p>Uma recarga automática está em processamento. Confirme o resultado antes de fazer outra compra.</p>
          <Button disabled={busy} onClick={handleResumeRecharge}>{busy ? 'Consultando...' : 'Consultar recarga em processamento'}</Button>
        </div> : <div className="space-y-3">
          <Button className="w-full" disabled={blocked} onClick={() => setExtrasOpen(true)}>Comprar plays extras</Button>
          <p className="text-xs text-muted-foreground">Use plays extras para continuar até a próxima renovação mensal.</p>
          {surplusPending && <p className="text-sm">Há uma compra de plays extras aguardando confirmação. Abra a compra de extras para consultar a mesma tentativa antes de renovar o plano.</p>}
          <Button className="w-full" variant="outline" disabled={blocked || surplusPending || !validQuote || planError} onClick={() => setConfirmRenewal(true)}>Renovar meu plano agora</Button>
          {pending && pending.action !== 'renew_now' && <a href="/checkout" className="block text-sm font-medium text-blue-600 underline">Consultar assinatura pendente no checkout</a>}
          {pending && <><p className="text-sm">Há um pagamento aguardando confirmação. A consulta usa o mesmo plano e cartão enviados na tentativa original.</p><Button className="w-full" disabled={busy || pending.action !== 'renew_now'} onClick={handleRenew}>Consultar renovação pendente</Button></>}
        </div>}
        {confirmRenewal && validQuote && !recharge?.processing && <div className="space-y-3 rounded-xl border p-4 text-sm">
          <p>Você autoriza uma cobrança imediata de {(renewal!.amount_cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} no cartão padrão para renovar o plano {plan?.nome}, com {renewal!.plays.toLocaleString('pt-BR')} plays por um mês completo.</p>
          <p>O novo ciclo começa hoje. A próxima cobrança passa para um mês a partir de hoje.</p>
          <Button className="w-full" disabled={blocked || surplusPending} onClick={handleRenew}>{busy ? 'Processando...' : 'Confirmar cobrança e renovar agora'}</Button>
          <Button className="w-full" variant="ghost" disabled={busy} onClick={() => setConfirmRenewal(false)}>Voltar</Button>
        </div>}
        <Button variant="ghost" disabled={busy} onClick={() => setDismissed(true)}>Aguardar a renovação mensal{plan?.data_renovacao ? ` (${new Date(plan.data_renovacao).toLocaleDateString('pt-BR')})` : ''}</Button>
      </DialogContent>
    </Dialog>
    <PlaysManagementModal open={extrasOpen && !!exhausted} onOpenChange={open => { setExtrasOpen(open); if (!open) void refreshBalances(); }} />
  </>;
}
